'use client';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Link from 'next/link';
import { PageSection, MoreLink } from '@/components';
import { MONO_FONT, SERIF_FONT } from '@/theme';
import { useStaggerIn } from '@/hooks/useStaggerIn';
import { serviceSegments, areasBySegment } from '@/libs/content/services';
import { copy } from '@/libs/content/copy';

/** Imagen del segmento (aspect 4/5, borde). Sin `image` cae a la textura. */
function SegmentImage({ image, label }) {
  return (
    <Box
      sx={{
        position: 'relative',
        aspectRatio: '4 / 5',
        border: '1px solid rgba(194,161,92,0.22)',
        display: 'flex',
        alignItems: 'flex-end',
        p: '22px',
        ...(image
          ? {
              // Baño navy sutil para integrar la foto con la paleta.
              backgroundImage: `linear-gradient(rgba(20,33,60,0.16),rgba(20,33,60,0.16)), url(${image})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }
          : {
              background:
                'repeating-linear-gradient(135deg,#1c2c4a,#1c2c4a 16px,#20335595 16px,#203355 32px)',
            }),
      }}
    >
      {!image && (
        <Box
          component="span"
          sx={{
            fontFamily: MONO_FONT,
            fontSize: '11px',
            letterSpacing: '0.14em',
            color: 'rgba(210,224,246,0.45)',
            textTransform: 'uppercase',
          }}
        >
          {label}
        </Box>
      )}
    </Box>
  );
}

/** Un área de práctica: fila de detalle con ancla. */
function PracticeArea({ area, number }) {
  return (
    <Box
      id={area.slug}
      component="article"
      sx={{
        borderTop: '1px solid rgba(194,161,92,0.25)',
        pt: { xs: 3.5, md: '28px' },
        pb: { xs: 3.5, md: '28px' },
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: '0.4fr 0.6fr' },
        gap: { xs: 2, md: 'clamp(24px, 4vw, 56px)' },
      }}
    >
      <Box>
        <Box
          component="span"
          sx={{
            fontFamily: MONO_FONT,
            fontSize: '12px',
            color: 'primary.main',
            letterSpacing: '0.1em',
          }}
        >
          {number}
        </Box>
        <Typography
          component="h3"
          sx={{
            fontFamily: SERIF_FONT,
            fontSize: 'clamp(20px, 2.2vw, 26px)',
            fontWeight: 500,
            color: 'primary.lighter',
            lineHeight: 1.2,
            mt: '8px',
          }}
        >
          {area.title}
        </Typography>
      </Box>

      <Box>
        <Typography
          sx={{
            fontSize: '15px',
            lineHeight: 1.75,
            color: 'text.onDark',
            fontWeight: 300,
          }}
        >
          {area.summary}
        </Typography>

        {area.comprende && (
          <Box
            component="ul"
            sx={{
              listStyle: 'none',
              p: 0,
              m: '18px 0 0',
              display: 'grid',
              gap: '10px',
            }}
          >
            {area.comprende.map((item, i) => (
              <Box
                key={i}
                component="li"
                sx={{
                  display: 'flex',
                  gap: '12px',
                  alignItems: 'flex-start',
                  fontSize: '14px',
                  lineHeight: 1.6,
                  color: 'text.onDarkMuted',
                }}
              >
                <Box
                  component="span"
                  sx={{ color: 'primary.main', mt: '2px', flexShrink: 0 }}
                >
                  —
                </Box>
                {item}
              </Box>
            ))}
          </Box>
        )}

        {area.closing && (
          <Typography
            sx={{
              mt: '18px',
              fontSize: '14.5px',
              lineHeight: 1.75,
              color: 'text.onDark',
              fontStyle: 'italic',
            }}
          >
            {area.closing}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

/** Un segmento: fila alternada (texto ↔ imagen) + sus áreas. */
function Segment({ segment, index }) {
  const areas = areasBySegment(segment.id);
  const reversed = index % 2 === 1;

  return (
    <Box id={segment.id} sx={{ mb: { xs: 8, md: 'clamp(72px, 9vw, 120px)' } }}>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
          gap: { xs: 4, md: 'clamp(30px, 5vw, 70px)' },
          alignItems: 'center',
          mb: { xs: 5, md: 8 },
        }}
      >
        <Box sx={{ order: { md: reversed ? 2 : 1 } }}>
          <Box
            component="span"
            sx={{
              fontFamily: MONO_FONT,
              fontSize: '13px',
              color: 'primary.main',
              letterSpacing: '0.1em',
            }}
          >
            {segment.number}
          </Box>
          <Typography
            variant="h2"
            component="h2"
            sx={{
              fontSize: 'clamp(26px, 3vw, 36px)',
              color: '#f6f1e8',
              mt: '10px',
              mb: '18px',
            }}
          >
            {segment.title}
          </Typography>
          <Typography
            sx={{
              fontSize: '16px',
              lineHeight: 1.8,
              color: 'text.onDarkMuted',
              fontWeight: 300,
            }}
          >
            {segment.intro}
          </Typography>
        </Box>
        <Box sx={{ order: { md: reversed ? 1 : 2 } }}>
          <SegmentImage
            image={segment.image}
            label={`imagen · ${segment.id}`}
          />
        </Box>
      </Box>

      {areas.map((area, i) => (
        <PracticeArea
          key={area.slug}
          area={area}
          number={String(i + 1).padStart(2, '0')}
        />
      ))}
    </Box>
  );
}

function SegmentSummaryCard({ segment }) {
  return (
    <Box
      component={Link}
      href={`/servicios/#${segment.id}`}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        border: '1px solid rgba(194,161,92,0.28)',
        p: { xs: '30px', md: '36px 34px' },
        color: 'inherit',
        transition: 'background 0.2s ease, border-color 0.2s ease',
        '&:hover': {
          background: 'rgba(194,161,92,0.06)',
          borderColor: 'rgba(194,161,92,0.5)',
        },
        '&:hover .svc-more': { gap: '14px' },
      }}
    >
      <Box
        component="span"
        sx={{ fontFamily: MONO_FONT, fontSize: '13px', color: 'primary.main' }}
      >
        {segment.number}
      </Box>
      <Typography
        component="h3"
        sx={{
          fontFamily: SERIF_FONT,
          // Achicado y sin quiebre: los 3 shortTitle entran en una línea
          // (el más largo es "Fundaciones y corporaciones").
          fontSize: { xs: '23px', md: '21px' },
          fontWeight: 500,
          color: 'primary.lighter',
          mt: '12px',
          mb: '14px',
          lineHeight: 1.25,
          whiteSpace: { md: 'nowrap' },
        }}
      >
        {segment.shortTitle || segment.title}
      </Typography>
      <Typography
        sx={{
          fontSize: '14px',
          lineHeight: 1.7,
          color: 'text.onDark',
          fontWeight: 300,
        }}
      >
        {segment.blurb}
      </Typography>

      <Box
        className="svc-more"
        component="span"
        aria-hidden="true"
        sx={{
          mt: 'auto',
          pt: '22px',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '8px',
          fontFamily: MONO_FONT,
          fontSize: '10.5px',
          fontWeight: 700,
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: 'primary.main',
          transition: 'gap 0.2s ease',
        }}
      >
        Ver más →
      </Box>
    </Box>
  );
}

export default function ServiciosSegments({
  id = 'servicios',
  variant = 'full',
  withHeader = true,
}) {
  const gridRef = useStaggerIn({ stagger: 0.1 });
  const { header, homeIntro } = copy.servicios;

  if (variant === 'summary') {
    return (
      <PageSection
        id={id}
        tone="navy"
        align="center"
        eyebrow={withHeader ? header.eyebrow : undefined}
        title={withHeader ? header.heading : undefined}
        lede={withHeader ? homeIntro : undefined}
      >
        <Box
          ref={gridRef}
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
            gap: 3,
          }}
        >
          {serviceSegments.map((s) => (
            <SegmentSummaryCard key={s.id} segment={s} />
          ))}
        </Box>
        <Box sx={{ textAlign: 'center', mt: { xs: 4, md: 6 } }}>
          <MoreLink href="/servicios/" tone="dark">
            {copy.home.more.servicios}
          </MoreLink>
        </Box>
      </PageSection>
    );
  }

  return (
    <PageSection
      id={id}
      tone="navy"
      eyebrow={withHeader ? header.eyebrow : undefined}
      title={withHeader ? header.heading : undefined}
    >
      <Box ref={gridRef}>
        {serviceSegments.map((s, i) => (
          <Segment key={s.id} segment={s} index={i} />
        ))}
      </Box>
    </PageSection>
  );
}
